import { AttributeTypes, DisplayMode } from '../../common/constants';
import { PiiQLSearch } from '../helpers/searchPiiQLGenerator';
import { AttributeFilter } from '../types/attributes';
import { FileType } from './file';

export enum SearchType {
  EXTERNAL = 'external',
  DEFAULT = 'default',
}

export enum PiiQLSearchType {
  // Some of the same types of attribute types
  // can be searched for
  DATE = 'Date',
  NUMERIC = 'Numeric',
  LINKS = 'Links', // Unknown if options are strings or itemIds
  BOOLEAN = 'Boolean',
  STRING = 'String',
  LABEL = 'Label',
  EXTERNAL_LINK = 'ExternalLink',
  // Plus additional frontend search types
  LINKS_LABEL = 'LinksLabel',
  LINKS_ITEM_ID = 'LinksItemId',
  NAME = 'Name',
  EXACT_VALUE = 'ExactValue',
}

export enum Operators {
  And = 'AND',
  Or = 'OR',
  Not = 'NOT',
}

/**
 * Static metadata attribute filters for files.
 * They are already compatible with 'FilterItem' so they can be used
 * to construct queries
 */
export const DetailsAttributeFilters: (AttributeFilter & PiiQLSearch.FilterItem)[] = [
  {
    alias: 'name',
    itemId: 'name',
    labelKey: 'dam.search.form.attributeFilters.name',
    label: {},
    searchPrefix: '',
    options: {
      multiSelect: false,
      allowGrouping: false,
      formatter: undefined,
      range: false,
    },
    attribute: {
      type: AttributeTypes.STRING,
      parameters: null,
      options: {
        enabled: true,
        required: false,
        searchLink: null,
        showLabel: true,
        type: AttributeTypes.STRING,
        visible: true,
        visibleCondition: null,
      },
      links: null,
      permission: null,
      alias: '',
      itemId: '',
    },
    _searchType: PiiQLSearchType.NAME,
    _queryFieldPath: 'Name',
  },
  {
    alias: 'guid',
    itemId: 'guid',
    labelKey: 'dam.search.form.attributeFilters.itemId',
    label: {},
    searchPrefix: '',
    options: {
      multiSelect: false,
      allowGrouping: false,
      formatter: undefined,
      range: false,
    },
    attribute: {
      type: AttributeTypes.STRING,
      parameters: null,
      options: {
        enabled: true,
        required: false,
        searchLink: null,
        showLabel: true,
        type: AttributeTypes.STRING,
        visible: true,
        visibleCondition: null,
      },
      links: null,
      permission: null,
      alias: '',
      itemId: '',
    },
    _searchType: PiiQLSearchType.STRING,
    _queryFieldPath: 'ItemId',
  },
  {
    alias: 'type',
    itemId: 'type',
    labelKey: 'dam.search.form.attributeFilters.type',
    label: {},
    searchPrefix: '',
    options: {
      multiSelect: false,
      allowGrouping: false,
      formatter: undefined,
      range: false,
    },
    attribute: {
      type: AttributeTypes.LINKS,
      parameters: null,
      _staticOptions: Object.values(FileType).map(type => ({
        attribute: '',
        values: [],
        label: {},
        createdDate: null,
        updatesDate: null,
        paths: null,
        itemId: type,
        alias: type,
        _labelKey: `dam.files.fileType.${type.toLowerCase()}`,
      })),
      options: {
        enabled: true,
        required: false,
        searchLink: null,
        showLabel: true,
        type: AttributeTypes.LINKS,
        visible: true,
        visibleCondition: null,
        displayMode: DisplayMode.Combo,
      },
      links: null,
      permission: null,
      alias: '',
      itemId: '',
    },
    _searchType: PiiQLSearchType.EXACT_VALUE,
    _queryFieldPath: 'Description.Type',
  },
  {
    alias: 'sizeRange',
    itemId: 'sizeRange',
    labelKey: '',
    label: {},
    searchPrefix: '',
    options: {
      multiSelect: false,
      allowGrouping: false,
      formatter: undefined,
      range: true,
      _startLabelKey: 'dam.search.form.attributeFilters.size.rangeStartLabel',
      _endLabelKey: 'dam.search.form.attributeFilters.size.rangeEndLabel',
      _valueLabelRangeFullKey: 'dam.search.form.attributeFilters.size.valueFull',
      _valueLabelRangeStartKey: 'dam.search.form.attributeFilters.size.valueStart',
      _valueLabelRangeEndKey: 'dam.search.form.attributeFilters.size.valueEnd',
    },
    attribute: {
      type: AttributeTypes.NUMERIC,
      parameters: null,
      options: {
        enabled: true,
        required: false,
        searchLink: null,
        showLabel: true,
        type: AttributeTypes.NUMERIC,
        visible: true,
        visibleCondition: null,
        minimum: 0,
      },
      links: null,
      permission: null,
      alias: '',
      itemId: '',
    },
    _searchType: PiiQLSearchType.NUMERIC,
    _queryFieldPath: 'Size',
  },
  {
    alias: 'createdDateRange',
    itemId: 'createdDateRange',
    labelKey: '',
    label: {},
    searchPrefix: '',
    options: {
      multiSelect: false,
      allowGrouping: false,
      formatter: undefined,
      range: true,
      _startLabelKey: 'dam.search.form.attributeFilters.importDate.rangeStartLabel',
      _endLabelKey: 'dam.search.form.attributeFilters.importDate.rangeEndLabel',
      _valueLabelRangeFullKey: 'dam.search.form.attributeFilters.importDate.valueFull',
      _valueLabelRangeStartKey: 'dam.search.form.attributeFilters.importDate.valueStart',
      _valueLabelRangeEndKey: 'dam.search.form.attributeFilters.importDate.valueEnd',
    },
    attribute: {
      type: AttributeTypes.DATE,
      parameters: null,
      options: {
        enabled: true,
        required: false,
        searchLink: null,
        showLabel: true,
        type: AttributeTypes.DATE,
        visible: true,
        visibleCondition: null,
      },
      links: null,
      permission: null,
      alias: '',
      itemId: '',
    },
    _searchType: PiiQLSearchType.DATE,
    _queryFieldPath: 'CreatedDate',
  },
  {
    alias: 'updatesDateRange',
    itemId: 'updatesDateRange',
    labelKey: '',
    label: {},
    searchPrefix: '',
    options: {
      multiSelect: false,
      allowGrouping: false,
      formatter: undefined,
      range: true,
      _startLabelKey: 'dam.search.form.attributeFilters.updatesDate.rangeStartLabel',
      _endLabelKey: 'dam.search.form.attributeFilters.updatesDate.rangeEndLabel',
      _valueLabelRangeFullKey: 'dam.search.form.attributeFilters.updatesDate.valueFull',
      _valueLabelRangeStartKey: 'dam.search.form.attributeFilters.updatesDate.valueStart',
      _valueLabelRangeEndKey: 'dam.search.form.attributeFilters.updatesDate.valueEnd',
    },
    attribute: {
      type: AttributeTypes.DATE,
      parameters: null,
      options: {
        enabled: true,
        required: false,
        searchLink: null,
        showLabel: true,
        type: AttributeTypes.DATE,
        visible: true,
        visibleCondition: null,
      },
      links: null,
      permission: null,
      alias: '',
      itemId: '',
    },
    _searchType: PiiQLSearchType.DATE,
    _queryFieldPath: 'UpdatesDate',
  },
];
